import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { AppProvider, Page, Card, Text, BlockStack } from '@shopify/polaris';
import { Provider as AppBridgeProvider, useAppBridge } from '@shopify/app-bridge-react';
import enTranslations from '@shopify/polaris/locales/en.json';
import AppNavigation from './components/Navigation';
import Breadcrumbs from './components/Breadcrumbs';
import Dashboard from './pages/Dashboard';
import Products from './pages/Products';
import ConditionsDashboard from './pages/ConditionsDashboard';
import ProductBookings from './pages/ProductBookings';
import AddOns from './pages/AddOns';
import Rentals from './pages/Rentals';
import CurrentRentals from './pages/CurrentRentals';
import CurrentlyRentedOut from './pages/CurrentlyRentedOut';
import {
  SHOPIFY_API_KEY,
  SKIP_APP_BRIDGE,
  getHost,
  setAppBridgeInstance,
} from './services/appBridge';

// Registers the App Bridge instance for use by the axios interceptors.
const AppBridgeRegistrar = () => {
  const app = useAppBridge();
  useEffect(() => {
    setAppBridgeInstance(app);
  }, [app]);
  return null;
};

const AppContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState('dashboard');

  // Handle shop storage from URL parameters (for OAuth flow)
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const shop = urlParams.get('shop');
    const installed = urlParams.get('installed');
    
    console.log('🔍 App.js - URL params:', { shop, installed });
    console.log('🔍 App.js - Current URL:', window.location.href);
    
    if (shop && installed === 'true') {
      console.log('🏪 Storing shop in localStorage:', shop);
      localStorage.setItem('shopify_shop', shop);
      
      // Clean up URL parameters
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
      console.log('✅ Shop stored and URL cleaned');
    } else {
      console.log('ℹ️  No shop/installed params found in URL');
    }
  }, [location.search]);

  // Update current page based on location
  useEffect(() => {
    const path = location.pathname;
    if (path === '/' || path === '/dashboard') {
      setCurrentPage('dashboard');
    } else if (path.startsWith('/products/') && path.endsWith('/bookings')) {
      setCurrentPage('products');
    } else if (path === '/products') {
      setCurrentPage('products');
    } else if (path === '/conditions') {
      setCurrentPage('conditions');
    } else if (path === '/addons') {
      setCurrentPage('addons');
    } else if (path === '/current-rentals') {
      setCurrentPage('current-rentals');
    } else if (path === '/currently-rented-out') {
      setCurrentPage('currently-rented-out');
    } else if (path === '/rentals') {
      setCurrentPage('rentals');
    } else if (path === '/calendar') {
      setCurrentPage('calendar');
    } else {
      setCurrentPage('dashboard');
    }
  }, [location]);

  const handleNavigate = (url) => {
    navigate(url);
  };

  const getBreadcrumbs = () => {
    const path = location.pathname;
    const breadcrumbs = [
      { label: 'Home', url: '/' }
    ];

    if (path.startsWith('/products/') && path.endsWith('/bookings')) {
      breadcrumbs.push({ label: 'Products', url: '/products' });
      breadcrumbs.push({ label: 'Bookings' });
    } else if (path === '/products') {
      breadcrumbs.push({ label: 'Products' });
    } else if (path === '/conditions') {
      breadcrumbs.push({ label: 'Conditions' });
    } else if (path === '/addons') {
      breadcrumbs.push({ label: 'Add-Ons' });
    } else if (path === '/current-rentals') {
      breadcrumbs.push({ label: 'Upcoming Rentals' });
    } else if (path === '/currently-rented-out') {
      breadcrumbs.push({ label: 'Currently Rented Out' });
    } else if (path === '/rentals') {
      breadcrumbs.push({ label: 'Rentals' });
    } else if (path === '/calendar') {
      breadcrumbs.push({ label: 'Calendar' });
    } else if (path === '/dashboard' || path === '/') {
      breadcrumbs.push({ label: 'Dashboard' });
    }

    return breadcrumbs;
  };

  return (
    <AppNavigation currentPage={currentPage} onNavigate={handleNavigate}>
      <div style={{ padding: '0 20px' }}>
        <Breadcrumbs items={getBreadcrumbs()} />
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/products" element={<Products />} />
          <Route path="/conditions" element={<ConditionsDashboard />} />
          <Route path="/products/:shopifyProductId/bookings" element={<ProductBookings />} />
          <Route path="/addons" element={<AddOns />} />
          <Route path="/rentals" element={<Rentals />} />
          <Route path="/current-rentals" element={<CurrentRentals />} />
          <Route path="/currently-rented-out" element={<CurrentlyRentedOut />} />
        </Routes>
      </div>
    </AppNavigation>
  );
};

const App = () => {
  const host = getHost();

  const routedContent = (
    <Router>
      <AppContent />
    </Router>
  );

  // Loaded outside of the Shopify admin: no `host` means App Bridge cannot
  // authenticate, so the admin API is unreachable. Show guidance instead of a
  // broken UI. Local dev can bypass with REACT_APP_SKIP_APP_BRIDGE=true.
  if (!host && !SKIP_APP_BRIDGE) {
    return (
      <AppProvider i18n={enTranslations}>
        <Page>
          <Card>
            <BlockStack gap="200">
              <Text as="h2" variant="headingMd">Open this app from your Shopify admin</Text>
              <Text as="p" tone="subdued">
                This app must be launched from Shopify admin → Apps so it can
                authenticate securely. Direct access is not permitted.
              </Text>
            </BlockStack>
          </Card>
        </Page>
      </AppProvider>
    );
  }

  return (
    <AppProvider i18n={enTranslations}>
      {host ? (
        <AppBridgeProvider config={{ apiKey: SHOPIFY_API_KEY, host, forceRedirect: true }}>
          <AppBridgeRegistrar />
          {routedContent}
        </AppBridgeProvider>
      ) : (
        routedContent
      )}
    </AppProvider>
  );
};

export default App;
